import type { InsightCard, RatioAssessment, RatioCard, RatioUnit } from '../../../data/ServerApiClient'
import type { CfoAnalysisGrounding, CfoAnalysisVisualSpec } from './analysis/types'
import { formatImpact, formatRatioValue } from './cfoFormat'
import { ratioBenchmarkAnchor, ratioCompanyValueForWindow, type RatioWindow } from './ratios/RatioDetailCard'

// Builders that turn the client-side data already on screen (a full RatioCard on
// the Ratios page, or an InsightCard on the Home brief) into (a) the instant
// `ratio_health` gauge visual and (b) the `grounding` payload the agent uses to
// answer with zero tool round-trips. Single source of truth for both sites.

/**
 * True display unit per key ratio. The Home insight card only carries
 * `impact.unit === 'ratio'`, so we recover the real unit here — a DSO seed
 * renders "45d" and a current-ratio seed "1.8x", not a percentage.
 */
export const RATIO_UNIT_BY_ID: Record<string, RatioUnit> = {
  current_ratio: 'times',
  quick_ratio: 'times',
  debt_to_equity: 'times',
  debt_to_assets: 'ratio',
  interest_coverage: 'times',
  days_sales_outstanding: 'days',
  days_inventory_outstanding: 'days',
  days_payable_outstanding: 'days',
  cash_conversion_cycle: 'days',
  return_on_equity: 'ratio',
  return_on_assets: 'ratio',
  gross_margin: 'ratio',
  net_margin: 'ratio',
  operating_margin: 'ratio',
}

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

const labelFromId = (metricId: string): string =>
  metricId
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const severityToAssessment = (sev: InsightCard['severity']): RatioAssessment => {
  switch (sev) {
    case 'bad':
      return 'concern'
    case 'watch':
    case 'check':
      return 'watch'
    case 'good':
      return 'healthy'
    default:
      return 'neutral'
  }
}

const impactUnitToValueKind = (
  unit: InsightCard['impact']['unit'],
): CfoAnalysisGrounding['value_kind'] => {
  switch (unit) {
    case 'usd':
      return 'currency'
    case 'pct':
    case 'pct_pts':
      return 'pct'
    case 'months':
      return 'months'
    case 'ratio':
      return 'ratio'
    default:
      return 'number'
  }
}

/** First→last summary of each component series, so the agent can name which input moved. */
export const componentSummaryFromRatioCard = (ratio: RatioCard): CfoAnalysisGrounding['components'] => {
  const trends = ratio.component_trends
  if (!trends || trends.length !== 2) return null
  return trends.map((series) => {
    // Match the dual-axis chart's 12-month tail so the agent's "rose X%" lines
    // up with what the user sees in the legend.
    const points = (series.trend || []).filter((p) => Number.isFinite(p.value)).slice(-12)
    const first = points.length ? points[0].value : null
    const last = points.length ? points[points.length - 1].value : null
    const delta_pct = first != null && first !== 0 && last != null ? (last - first) / Math.abs(first) : null
    const top_accounts = (series.breakdown || []).slice(0, 5).map((a) => ({
      label: a.label,
      value: a.value,
      delta_abs: a.delta_abs ?? null,
      share: a.share ?? null,
    }))
    return { label: series.label, role: series.role, first, last, delta_pct, top_accounts }
  })
}

const insightDrillParams = (card: InsightCard): Record<string, unknown> =>
  (card.drill_target?.params as Record<string, unknown>) || {}

/** A ratio detector card carries a healthy band we can render as a gauge. */
const isRatioInsightCard = (card: InsightCard): boolean => {
  if (card.detector === 'ratio_health') return true
  const params = insightDrillParams(card)
  return num(params.healthy_min) != null || num(params.healthy_max) != null
}

// ---------------------------------------------------------------------------
// Home brief insight card → visual + grounding
// ---------------------------------------------------------------------------

/** Instant ratio-health gauge for a ratio insight card; null for non-ratio cards. */
export function ratioHealthVisualFromInsightCard(card: InsightCard): CfoAnalysisVisualSpec | null {
  if (!isRatioInsightCard(card)) return null
  const value = num(card.impact.value)
  if (value == null) return null
  const metricId = String(card.impact.metric_id || '')
  const unit: RatioUnit = RATIO_UNIT_BY_ID[metricId] || 'ratio'
  const params = insightDrillParams(card)
  return {
    kind: 'ratio_health',
    label: metricId ? labelFromId(metricId) : card.headline,
    display: formatRatioValue(value, unit),
    value,
    healthyMin: num(params.healthy_min),
    healthyMax: num(params.healthy_max),
    higherIsBetter: typeof params.higher_is_better === 'boolean' ? params.higher_is_better : null,
    unit,
    assessment: severityToAssessment(card.severity),
    benchmark: null,
    trend: null,
    formula: null,
    interpretation: card.body || null,
    caption: null,
  }
}

/** Grounding for any insight card (ratio or not), so the agent answers instantly. */
export function groundingFromInsightCard(card: InsightCard, period: string | null): CfoAnalysisGrounding {
  const params = insightDrillParams(card)
  const metricId = String(card.impact.metric_id || '') || null
  const ratioUnit = metricId ? RATIO_UNIT_BY_ID[metricId] : undefined
  const value = num(card.impact.value)
  return {
    metric_id: metricId,
    metric_label: metricId ? labelFromId(metricId) : null,
    value_kind: ratioUnit ?? impactUnitToValueKind(card.impact.unit),
    latest: value,
    latest_period: period,
    display: value != null ? (ratioUnit ? formatRatioValue(value, ratioUnit) : formatImpact(card)) : null,
    healthy_min: num(params.healthy_min),
    healthy_max: num(params.healthy_max),
    higher_is_better: typeof params.higher_is_better === 'boolean' ? params.higher_is_better : null,
    assessment: severityToAssessment(card.severity),
    benchmark_basis: null,
    benchmark_label: null,
    formula: null,
    interpretation: card.body || null,
    trend: null,
    mom:
      card.impact.comparison === 'mom' && value != null
        ? { prior: null, current: value, delta_abs: card.impact.unit === 'usd' ? value : null, delta_pct: null }
        : null,
    components: null,
    comparisons: null,
    source: 'insight_card',
  }
}

// ---------------------------------------------------------------------------
// Ratios page RatioCard → visual + grounding (richer: trend, benchmark, formula)
// ---------------------------------------------------------------------------

export function ratioHealthVisualFromRatioCard(ratio: RatioCard, ratioWindow: RatioWindow): CfoAnalysisVisualSpec {
  const value = ratio.latest ?? ratioCompanyValueForWindow(ratio, ratioWindow)
  return {
    kind: 'ratio_health',
    label: ratio.label,
    display: formatRatioValue(value, ratio.unit),
    value: num(value) ?? Number.NaN,
    healthyMin: ratio.benchmark.healthy_min,
    healthyMax: ratio.benchmark.healthy_max,
    higherIsBetter: ratio.higher_is_better,
    unit: ratio.unit,
    assessment: ratio.assessment,
    benchmark: ratioBenchmarkAnchor(ratio),
    trend: (ratio.trend || []).slice(-12).map((point) => point.value),
    formula: ratio.formula || null,
    interpretation: ratio.ai_interpretation || ratio.interpretation || null,
    caption: null,
  }
}

export function groundingFromRatioCard(ratio: RatioCard, ratioWindow: RatioWindow): CfoAnalysisGrounding {
  const value = ratio.latest ?? ratioCompanyValueForWindow(ratio, ratioWindow)
  const mom = (ratio.comparisons || []).find((c) => c.kind === 'mom') || null
  return {
    metric_id: ratio.id,
    metric_label: ratio.label,
    value_kind: ratio.unit,
    latest: num(value),
    latest_period: ratio.latest_period || null,
    display: formatRatioValue(value, ratio.unit),
    healthy_min: ratio.benchmark.healthy_min,
    healthy_max: ratio.benchmark.healthy_max,
    higher_is_better: ratio.higher_is_better,
    assessment: ratio.assessment,
    benchmark_basis: ratio.benchmark.basis || null,
    benchmark_label: ratio.benchmark.source_label || ratio.benchmark.industry_label || null,
    formula: ratio.formula || null,
    interpretation: ratio.ai_interpretation || ratio.interpretation || null,
    trend: (ratio.trend || []).slice(-12),
    mom: mom
      ? { prior: mom.prior, current: mom.current, delta_abs: mom.delta_abs, delta_pct: mom.delta_pct }
      : null,
    components: componentSummaryFromRatioCard(ratio),
    comparisons: (ratio.comparisons || [])
      .filter((c) => c.current != null || c.prior != null)
      .map((c) => ({ label: c.label, current: c.current, prior: c.prior, delta_pct: c.delta_pct })),
    source: 'ratio_card',
  }
}
